import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Send, Loader2, Sparkles, Crown, Lock, Plus, MessageSquare, Zap, ShieldCheck, Clock, Share2, Copy, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../Header';
import LoginModal from '../LoginModal';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TUTOR_IMG = 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/e18339bd6843869b4325b2dc9f6a410db3e3978e78549fff3cb8083bc3082bfb.jpeg';

const MarkdownMessage = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
      ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
      ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
      li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
      strong: ({ node, ...props }) => <strong className="font-semibold text-white" {...props} />,
      h1: ({ node, ...props }) => <h1 className="text-base font-bold text-white mt-2 mb-1" {...props} />,
      h2: ({ node, ...props }) => <h2 className="text-sm font-bold text-white mt-2 mb-1" {...props} />,
      h3: ({ node, ...props }) => <h3 className="text-sm font-semibold text-white mt-2 mb-1" {...props} />,
      code: ({ node, inline, ...props }) => inline
        ? <code className="bg-black/40 text-indigo-200 rounded px-1 py-0.5 text-[12px]" {...props} />
        : <code className="block bg-black/50 text-indigo-100 rounded-lg p-3 my-2 text-[12px] overflow-x-auto" {...props} />,
      a: ({ node, ...props }) => <a className="text-indigo-300 underline" target="_blank" rel="noopener noreferrer" {...props} />,
      blockquote: ({ node, ...props }) => <blockquote className="border-l-2 border-indigo-500/50 pl-3 italic text-gray-300 my-2" {...props} />,
    }}
  >
    {text}
  </ReactMarkdown>
);

const SUGGESTIONS = [
  'Como crio uma família paramétrica de porta no Revit?',
  'Qual a diferença entre worksets e vínculos no Revit?',
  'Como exportar meu modelo para IFC corretamente?',
  'Como criar uma tabela de quantitativos (levantamento)?',
];

const BimIAPage = () => {
  const { isAuthenticated } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const [paywall, setPaywall] = useState(null); // 'subscription_required' | 'quota_exceeded' | null
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [popular, setPopular] = useState([]);
  const [topQuestions, setTopQuestions] = useState([]);
  const endRef = useRef(null);
  const typingRef = useRef(null);

  useEffect(() => () => { if (typingRef.current) clearTimeout(typingRef.current); }, []);

  const revealAnswer = useCallback((fullText) => {
    setMessages((m) => [...m, { role: 'assistant', content: '' }]);
    setTyping(true);
    const tokens = fullText.split(/(\s+)/);
    let i = 0;
    return new Promise((resolve) => {
      const step = () => {
        i += 2;
        const partial = tokens.slice(0, i).join('');
        setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { role: 'assistant', content: partial };
          return c;
        });
        if (i < tokens.length) {
          typingRef.current = setTimeout(step, 26);
        } else {
          setMessages((m) => {
            const c = [...m];
            c[c.length - 1] = { role: 'assistant', content: fullText };
            return c;
          });
          setTyping(false);
          resolve();
        }
      };
      typingRef.current = setTimeout(step, 26);
    });
  }, []);

  const loadStatus = useCallback(async () => {
    if (!isAuthenticated) { setStatus(null); return; }
    try {
      const { data } = await axios.get(`${API}/bimia/status`);
      setStatus(data);
    } catch { setStatus(null); }
  }, [isAuthenticated]);

  const loadSessions = useCallback(async () => {
    if (!isAuthenticated) { setSessions([]); return; }
    try {
      const { data } = await axios.get(`${API}/bimia/sessions`);
      setSessions(data || []);
    } catch { setSessions([]); }
  }, [isAuthenticated]);

  useEffect(() => { loadStatus(); loadSessions(); }, [loadStatus, loadSessions]);
  useEffect(() => {
    axios.get(`${API}/bimia/pricing`).then(({ data }) => setPricing(data?.pricing || null)).catch(() => {});
  }, []);
  useEffect(() => {
    axios.get(`${API}/bimia/popular`).then(({ data }) => setPopular(data?.items || [])).catch(() => {});
  }, []);
  useEffect(() => {
    axios.get(`${API}/bimia/top-questions`).then(({ data }) => setTopQuestions(data?.items || [])).catch(() => {});
  }, []);  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending, typing]);

  const openSession = async (sid) => {
    try {
      const { data } = await axios.get(`${API}/bimia/sessions/${sid}`);
      setSessionId(sid);
      setMessages(data.messages || []);
      setPaywall(null);
    } catch { toast.error('Não foi possível abrir a conversa.'); }
  };

  const newChat = () => { setSessionId(null); setMessages([]); setPaywall(null); };

  const shareChat = async () => {
    if (!sessionId) return;
    setSharing(true);
    try {
      const { data } = await axios.post(`${API}/bimia/sessions/${sessionId}/share`);
      const url = `${window.location.origin}${data.path}`;
      setShareUrl(url);
      setCopied(false);
      setShareOpen(true);
    } catch {
      toast.error('Não foi possível gerar o link de compartilhamento.');
    } finally {
      setSharing(false);
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const shareText = 'Olha essa conversa com o tutor de IA de Revit do AJ | BIM IA:';

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending || typing) return;
    if (!isAuthenticated) { setLoginOpen(true); return; }
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/bimia/chat`, { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setStatus(data.status);
      setSending(false);
      await revealAnswer(data.answer);
      loadSessions();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) {
        setMessages((m) => m.slice(0, -1)); // remove a pergunta não respondida
        setPaywall(detail === 'quota_exceeded' ? 'quota_exceeded' : 'subscription_required');
        loadStatus();
      } else {
        toast.error('Erro ao falar com a IA. Tente novamente.');
        setMessages((m) => m.slice(0, -1));
      }
    } finally {
      setSending(false);
    }
  };

  const hasAccess = status?.access;
  const remaining = status?.remaining ?? 0;

  const promptChips = (() => {
    const seen = new Set();
    const out = [];
    for (const q of topQuestions) {
      const t = (q.text || '').trim();
      const k = t.toLowerCase();
      if (t && !seen.has(k)) { seen.add(k); out.push(t); }
      if (out.length >= 6) break;
    }
    for (const q of SUGGESTIONS) {
      if (out.length >= 6) break;
      if (!seen.has(q.toLowerCase())) { seen.add(q.toLowerCase()); out.push(q); }
    }
    return out;
  })();
  const chipLabel = (t) => (t.length > 72 ? t.slice(0, 72).trimEnd() + '…' : t);

  return (
    <div className="min-h-screen bg-black" data-testid="bimia-page">
      <Header onLoginClick={() => setLoginOpen(true)} />
      <LoginModal isOpen={loginOpen} onClose={() => { setLoginOpen(false); loadStatus(); loadSessions(); }} />

      {/* Hero */}
      <section className="relative pt-28 pb-10 bg-gradient-to-b from-indigo-950/40 via-zinc-900 to-black overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-96 h-96 bg-indigo-600 rounded-full blur-3xl" />
          <div className="absolute top-10 right-1/4 w-80 h-80 bg-violet-600 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-indigo-600/15 border border-indigo-500/30 text-indigo-300 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles size={14} /> Novo · Suporte com IA
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">AJ | BIM <span className="text-indigo-400">IA</span></h1>
          <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto mb-6">
            Seu suporte técnico imediato de <strong className="text-white">Revit</strong>. Pergunte qualquer dúvida sobre a última versão e receba orientação passo a passo, na hora, a qualquer momento.
          </p>
          <p className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto mb-6 leading-relaxed" data-testid="bimia-hero-pitch">
            Além da maior e mais completa formação profissional em Revit em língua portuguesa, você terá um <strong className="text-indigo-300">tutor de IA treinado em toda a metodologia da AJ</strong>, disponível 24 horas por dia, 365 dias por ano. Faça suas perguntas e deixe a IA lhe responder e orientar, como um professor adicional.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-400">
            <span className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-indigo-400" /> Baseado na Formação Revit Expert</span>
            <span className="flex items-center gap-1.5"><Clock size={16} className="text-indigo-400" /> Disponível 24/7</span>
            <span className="flex items-center gap-1.5"><Zap size={16} className="text-indigo-400" /> Respostas objetivas</span>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 lg:px-8 py-6">
        <div className="grid lg:grid-cols-4 gap-4">
          {/* Sidebar de conversas */}
          <aside className="lg:col-span-1 space-y-3" data-testid="bimia-sidebar">
            <Button onClick={newChat} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="bimia-new-chat">
              <Plus size={16} className="mr-2" /> Nova conversa
            </Button>
            {status && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-xs" data-testid="bimia-quota">
                {hasAccess ? (
                  <>
                    <div className="flex items-center gap-1.5 text-green-400 font-semibold mb-1">
                      <Crown size={14} /> {status.via_active_student ? 'IA da Formação' : 'Acesso ativo'}
                    </div>
                    {status.via_active_student && <div className="text-indigo-300 mb-1">Incluída na sua Formação Revit Expert 🎓</div>}
                    <div className="text-gray-400">Perguntas restantes este mês: <strong className="text-white">{remaining}</strong></div>
                    {status.extra_credits > 0 && <div className="text-gray-500 mt-0.5">Inclui {status.extra_credits} crédito(s) de aditivo</div>}
                  </>
                ) : (
                  <div className="text-gray-400">
                    {status.trial_used
                      ? <span className="flex items-center gap-1.5 text-amber-300"><Lock size={13} /> Teste grátis usado</span>
                      : <span className="flex items-center gap-1.5 text-indigo-300"><Sparkles size={13} /> Você tem 1 pergunta grátis</span>}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
              {sessions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => openSession(s.id)}
                  className={`w-full text-left flex items-start gap-2 rounded-lg px-3 py-2 text-xs transition-colors ${sessionId === s.id ? 'bg-indigo-600/20 border border-indigo-500/40 text-white' : 'bg-zinc-900 border border-zinc-800 text-gray-300 hover:border-indigo-500/40'}`}
                  data-testid={`bimia-session-${s.id}`}
                >
                  <MessageSquare size={13} className="mt-0.5 shrink-0 text-indigo-400" />
                  <span className="truncate">{s.title}</span>
                </button>
              ))}
            </div>
          </aside>

          {/* Chat */}
          <main className="lg:col-span-3">
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl flex flex-col h-[560px]" data-testid="bimia-chat">
              {sessionId && messages.length > 0 && (
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800" data-testid="bimia-chat-toolbar">
                  <span className="text-xs text-gray-400 flex items-center gap-1.5">
                    <Bot size={14} className="text-indigo-400" /> Conversa com a IA
                  </span>
                  <button
                    onClick={shareChat}
                    disabled={sharing}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-300 hover:text-white bg-indigo-600/15 hover:bg-indigo-600/30 border border-indigo-500/30 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60"
                    data-testid="bimia-share-btn"
                  >
                    {sharing ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />} Compartilhar
                  </button>
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 && (
                  <div className="relative h-full min-h-[420px] rounded-xl overflow-hidden" data-testid="bimia-empty-state">
                    <img src={TUTOR_IMG} alt="Tutor de IA ensinando Revit para um profissional" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/10" />
                    <div className="relative h-full flex flex-col justify-end p-5 md:p-6">
                      <div className="inline-flex items-center gap-1.5 self-start bg-indigo-600/25 border border-indigo-500/40 text-indigo-200 rounded-full px-3 py-1 text-[11px] font-semibold mb-3">
                        <Bot size={13} /> Seu tutor de IA de Revit
                      </div>
                      <h3 className="text-white font-bold text-xl md:text-2xl mb-1 max-w-lg">Pergunte qualquer coisa sobre Revit</h3>
                      <p className="text-indigo-300 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5" data-testid="bimia-chips-heading">
                        <Sparkles size={13} /> Principais assuntos solicitados à IA
                      </p>
                      <div className="flex flex-wrap gap-2 max-w-2xl">
                        {promptChips.map((q) => (
                          <button key={q} onClick={() => send(q)} title={q} className="text-xs bg-black/50 hover:bg-indigo-600/50 border border-white/15 hover:border-indigo-400/60 text-gray-100 rounded-full px-3 py-1.5 backdrop-blur-sm transition-colors text-left" data-testid="bimia-suggestion">
                            {chipLabel(q)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={`${m.role}-${i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'bg-indigo-600 text-white whitespace-pre-wrap' : 'bg-zinc-800 text-gray-100 border border-zinc-700'}`} data-testid={`bimia-msg-${m.role}`}>
                      {m.role === 'assistant'
                        ? <div><MarkdownMessage text={m.content || ''} />{typing && i === messages.length - 1 && <span className="inline-block w-1.5 h-4 -mb-0.5 ml-0.5 bg-indigo-400 animate-pulse" />}</div>
                        : m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-3 text-sm text-gray-400 flex items-center gap-2">
                      <Loader2 size={15} className="animate-spin text-indigo-400" /> A IA está pensando...
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {/* Paywall */}
              {paywall && (
                <div className="border-t border-indigo-500/30 bg-indigo-950/40 p-4" data-testid="bimia-paywall">
                  <div className="flex items-start gap-3">
                    <Lock className="text-indigo-400 mt-0.5 shrink-0" size={20} />
                    <div className="flex-1">
                      <div className="text-white font-semibold text-sm">
                        {paywall === 'quota_exceeded' ? 'Você atingiu o limite mensal de perguntas' : 'Assine para continuar perguntando'}
                      </div>
                      <p className="text-gray-300 text-xs mt-0.5">
                        {paywall === 'quota_exceeded'
                          ? 'Contrate um aditivo de perguntas para continuar usando o AJ | BIM IA este mês.'
                          : 'O AJ | BIM IA é exclusivo para assinantes — e já está incluso no plano Premium do BIM Connect e grátis para alunos ativos da Formação.'}
                      </p>
                      <button
                        onClick={() => document.getElementById('bimia-planos')?.scrollIntoView({ behavior: 'smooth' })}
                        className="mt-3 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 h-9 text-sm font-medium"
                        data-testid="bimia-view-plans"
                      >
                        <Crown size={15} /> Ver planos
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="border-t border-zinc-800 p-3">
                {!isAuthenticated ? (
                  <Button onClick={() => setLoginOpen(true)} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11" data-testid="bimia-login-cta">
                    Entrar para usar o AJ | BIM IA
                  </Button>
                ) : (
                  <div className="flex items-end gap-2">
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder="Escreva sua dúvida sobre Revit..."
                      rows={1}
                      disabled={sending || typing}
                      className="flex-1 resize-none bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 max-h-32"
                      data-testid="bimia-input"
                    />
                    <Button onClick={() => send()} disabled={sending || typing || !input.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white h-11 w-11 p-0 shrink-0" data-testid="bimia-send">
                      {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Perguntas populares */}
      {popular.length > 0 && (
        <section className="py-14 px-4 border-t border-zinc-900" data-testid="bimia-popular-section">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-indigo-600/15 border border-indigo-500/30 text-indigo-300 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider mb-3">
                <Sparkles size={14} /> Perguntas populares
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Veja o que a IA já respondeu</h2>
              <p className="text-gray-400 text-sm max-w-2xl mx-auto">Conversas reais compartilhadas pela comunidade. Explore e depois faça a sua própria pergunta.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {popular.map((p) => (
                <Link
                  key={p.token}
                  to={p.path}
                  className="group bg-zinc-900/60 border border-zinc-800 hover:border-indigo-500/50 rounded-2xl overflow-hidden flex flex-col transition-colors"
                  data-testid={`bimia-popular-${p.token}`}
                >
                  {p.image && (
                    <div className="h-40 w-full overflow-hidden bg-black">
                      <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-testid={`bimia-popular-img-${p.token}`} />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="bg-indigo-600/20 border border-indigo-500/40 rounded-lg p-1.5"><MessageSquare size={14} className="text-indigo-300" /></div>
                      <h3 className="text-white font-semibold text-sm leading-snug line-clamp-2">{p.title}</h3>
                    </div>
                    <p className="text-gray-400 text-xs leading-relaxed line-clamp-3 flex-1">{p.snippet}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-indigo-300 group-hover:text-white transition-colors">
                      Ver conversa <Send size={12} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Planos */}
      <section id="bimia-planos" className="py-14 px-4 border-t border-zinc-900 bg-gradient-to-b from-transparent to-indigo-950/20">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-2">Planos do AJ | BIM <span className="text-indigo-400">IA</span></h2>
            <p className="text-gray-400 text-sm max-w-2xl mx-auto">Comece grátis e evolua para um plano anual com acesso completo ao seu tutor de IA de Revit.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { key: 'gratuito', tag: 'active_student', name: 'Gratuito', price: 'R$ 0', period: '', desc: 'Acesso garantido aos alunos ativos da Formação Revit Expert (e 1 pergunta grátis para todos).', accent: 'border-green-500/50', url: null, ctaFree: true },
              { key: 'alumni_monthly', tag: 'alumni', name: 'Ex-aluno · Mensal', price: 'R$ 49,90', period: '/mês', desc: 'Preço especial mensal para quem já é aluno/ex-aluno.', accent: 'border-blue-500/50', url: pricing?.alumni_monthly?.checkout_url },
              { key: 'alumni_yearly', tag: 'alumni', name: 'Ex-aluno · Anual', price: 'R$ 397', period: '/ano', installments: 'ou 12x', desc: 'Plano anual com desconto para ex-alunos.', accent: 'border-blue-500', url: pricing?.alumni_yearly?.checkout_url },
              { key: 'external_monthly', tag: 'external', name: 'Membro Externo · Mensal', price: 'R$ 89,90', period: '/mês', desc: 'Acesso mensal para o público geral.', accent: 'border-zinc-700', url: pricing?.external_monthly?.checkout_url },
              { key: 'external_yearly', tag: 'external', name: 'Membro Externo · Anual', price: 'R$ 497', period: '/ano', installments: 'ou 12x', desc: 'Melhor custo-benefício para o público externo.', accent: 'border-indigo-500', popular: true, url: pricing?.external_yearly?.checkout_url },
            ].map((p) => {
              const mine = status?.audience === p.tag;
              return (
                <div key={p.key} className={`relative bg-zinc-900/60 rounded-2xl border ${p.popular ? 'border-indigo-500' : p.accent} p-5 flex flex-col`} data-testid={`bimia-plan-${p.key}`}>
                  {p.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">MELHOR VALOR</span>}
                  {mine && <span className="absolute top-3 right-3 bg-green-500/20 text-green-300 text-[10px] px-2 py-0.5 rounded-full border border-green-500/40">Seu perfil</span>}
                  <h3 className="text-white font-semibold">{p.name}</h3>
                  <div className="mt-2 mb-1">
                    <span className="text-3xl font-bold text-white">{p.price}</span>
                    <span className="text-gray-400 text-sm"> {p.period}</span>
                  </div>
                  {p.installments && <div className="text-gray-500 text-xs mb-1">{p.installments} na Hotmart</div>}
                  <p className="text-gray-400 text-xs flex-1 mb-4 mt-1">{p.desc}</p>
                  {p.ctaFree ? (
                    hasAccess
                      ? <Button disabled className="bg-green-600/20 text-green-300 border border-green-500/40 h-9 cursor-default"><Sparkles size={15} className="mr-2" /> Acesso ativo</Button>
                      : <Button onClick={() => { if (!isAuthenticated) { setLoginOpen(true); } else { window.scrollTo({ top: 0, behavior: 'smooth' }); } }} className="bg-zinc-700 hover:bg-zinc-600 text-white h-9" data-testid="bimia-plan-gratuito-cta">Começar grátis</Button>
                  ) : p.url ? (
                    <Button onClick={() => window.open(p.url, '_blank', 'noopener')} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9" data-testid={`bimia-subscribe-${p.key}`}>
                      <Crown size={15} className="mr-2" /> Assinar
                    </Button>
                  ) : (
                    <Button disabled className="bg-zinc-700 text-gray-300 h-9 cursor-not-allowed" data-testid={`bimia-subscribe-soon-${p.key}`}>
                      <Clock size={15} className="mr-2" /> Em breve
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-center text-gray-500 text-xs mt-6">Alunos ativos da Formação Revit Expert e assinantes Premium do BIM Connect têm a IA incluída.</p>
        </div>
      </section>

      {shareOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShareOpen(false)} data-testid="bimia-share-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="bg-indigo-600/20 border border-indigo-500/40 rounded-lg p-2"><Share2 size={18} className="text-indigo-300" /></div>
                <div>
                  <h3 className="text-white font-semibold text-base">Compartilhar conversa</h3>
                  <p className="text-gray-500 text-xs">Qualquer pessoa com o link poderá ver esta conversa.</p>
                </div>
              </div>
              <button onClick={() => setShareOpen(false)} className="text-gray-500 hover:text-white" data-testid="bimia-share-close"><X size={18} /></button>
            </div>

            <div className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-lg p-1.5 mb-4">
              <input readOnly value={shareUrl} className="flex-1 bg-transparent text-gray-300 text-xs px-2 focus:outline-none truncate" data-testid="bimia-share-url" />
              <button onClick={copyShare} className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-md px-3 py-2 shrink-0" data-testid="bimia-share-copy">
                {copied ? <><Check size={13} /> Copiado</> : <><Copy size={13} /> Copiar</>}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <a href={`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-gray-200 text-xs rounded-lg py-2.5 transition-colors" data-testid="bimia-share-whatsapp">WhatsApp</a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-gray-200 text-xs rounded-lg py-2.5 transition-colors" data-testid="bimia-share-linkedin">LinkedIn</a>
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-gray-200 text-xs rounded-lg py-2.5 transition-colors" data-testid="bimia-share-x">X / Twitter</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BimIAPage;
